/**
 * Tiny 8×8 pixel icons for the trade-card resource summary. Generated on
 * first use into a Phaser texture so OfferCard can `add.image` them with
 * no per-frame draw cost.
 *
 * Why generate in code rather than preload from disk: the icons are
 * trivially small, share the existing palette, and live next to the
 * resource enum — keeps the licence audit short (no extra art assets).
 */

import Phaser from "phaser";

import { PALETTE } from "../config";
import type { Resource, SkillId } from "../types";
import { t } from "../i18n";

const SIZE = 8;

type IconKey =
  | "shopIcon.geo"
  | "shopIcon.hp"
  | "shopIcon.feather"
  | "shopIcon.scroll"
  | "shopIcon.skillDoubleJump"
  | "shopIcon.skillEscapeFeather";

function ensureIcon(scene: Phaser.Scene, key: IconKey, draw: (ctx: CanvasRenderingContext2D) => void): void {
  if (scene.textures.exists(key)) return;
  const tex = scene.textures.createCanvas(key, SIZE, SIZE);
  if (!tex) return;
  draw(tex.getContext());
  tex.refresh();
}

function rgb(hex: number): string {
  return `#${hex.toString(16).padStart(6, "0")}`;
}

function clear(ctx: CanvasRenderingContext2D): void {
  ctx.clearRect(0, 0, SIZE, SIZE);
}

export function ensureAllShopIcons(scene: Phaser.Scene): void {
  // Geo: gold coin disc.
  ensureIcon(scene, "shopIcon.geo", (ctx) => {
    clear(ctx);
    ctx.fillStyle = rgb(PALETTE.geo);
    ctx.fillRect(1, 0, 6, 1);
    ctx.fillRect(0, 1, 8, 6);
    ctx.fillRect(1, 7, 6, 1);
    ctx.fillStyle = "#7a5320";
    ctx.fillRect(1, 1, 1, 6); // shading
    ctx.fillRect(6, 1, 1, 6);
  });

  // HP: pixel heart.
  ensureIcon(scene, "shopIcon.hp", (ctx) => {
    clear(ctx);
    ctx.fillStyle = rgb(PALETTE.heart);
    ctx.fillRect(1, 1, 2, 1);
    ctx.fillRect(5, 1, 2, 1);
    ctx.fillRect(0, 2, 8, 2);
    ctx.fillRect(1, 4, 6, 1);
    ctx.fillRect(2, 5, 4, 1);
    ctx.fillRect(3, 6, 2, 1);
  });

  // Feather: pale-blue plume.
  ensureIcon(scene, "shopIcon.feather", (ctx) => {
    clear(ctx);
    ctx.fillStyle = rgb(PALETTE.feather);
    ctx.fillRect(4, 0, 2, 1);
    ctx.fillRect(3, 1, 3, 1);
    ctx.fillRect(2, 2, 4, 1);
    ctx.fillRect(2, 3, 4, 1);
    ctx.fillRect(1, 4, 5, 1);
    ctx.fillRect(1, 5, 4, 1);
    ctx.fillStyle = rgb(PALETTE.featherShaft);
    ctx.fillRect(3, 5, 1, 3);
  });

  // Scroll: vertical parchment roll. Reads as a scroll rather than as
  // an `=` sign (the old horizontal-stripe version was confusing).
  ensureIcon(scene, "shopIcon.scroll", (ctx) => {
    clear(ctx);
    // Top + bottom rolled caps (dark ink).
    ctx.fillStyle = rgb(PALETTE.ink);
    ctx.fillRect(1, 0, 6, 1);
    ctx.fillRect(1, 7, 6, 1);
    // Parchment body.
    ctx.fillStyle = rgb(PALETTE.scroll);
    ctx.fillRect(2, 1, 4, 6);
    // Two short tick marks reading as "writing" on the scroll.
    ctx.fillStyle = rgb(PALETTE.ink);
    ctx.fillRect(3, 3, 2, 1);
    ctx.fillRect(3, 5, 2, 1);
    // Edge wisps.
    ctx.fillStyle = rgb(PALETTE.parchmentShadow);
    ctx.fillRect(1, 1, 1, 6);
    ctx.fillRect(6, 1, 1, 6);
  });

  // Skill: double-jump (two stacked up-arrows).
  ensureIcon(scene, "shopIcon.skillDoubleJump", (ctx) => {
    clear(ctx);
    ctx.fillStyle = rgb(PALETTE.skillUnlocked);
    // upper chevron
    ctx.fillRect(3, 0, 2, 1);
    ctx.fillRect(2, 1, 4, 1);
    ctx.fillRect(1, 2, 6, 1);
    // lower chevron
    ctx.fillRect(3, 4, 2, 1);
    ctx.fillRect(2, 5, 4, 1);
    ctx.fillRect(1, 6, 6, 1);
  });

  // Skill: escape feather (single feather, gold-rimmed).
  ensureIcon(scene, "shopIcon.skillEscapeFeather", (ctx) => {
    clear(ctx);
    ctx.fillStyle = rgb(PALETTE.feather);
    ctx.fillRect(3, 0, 2, 1);
    ctx.fillRect(2, 1, 4, 1);
    ctx.fillRect(1, 2, 6, 3);
    ctx.fillRect(2, 5, 4, 1);
    ctx.fillStyle = rgb(PALETTE.geo);
    ctx.fillRect(3, 2, 2, 1); // gold seal
    ctx.fillStyle = rgb(PALETTE.featherShaft);
    ctx.fillRect(3, 6, 1, 2);
  });
}

export function iconKeyForResource(r: Resource): IconKey {
  switch (r.kind) {
    case "geo":
      return "shopIcon.geo";
    case "hp":
      return "shopIcon.hp";
    case "feather":
      return "shopIcon.feather";
    case "inventoryItem":
      return "shopIcon.scroll";
    case "skill":
      return r.skillId === "doubleJump" ? "shopIcon.skillDoubleJump" : "shopIcon.skillEscapeFeather";
  }
}

/**
 * Concise readout for an aggregated resource entry. `count` is the number
 * of times a discrete resource appears (only meaningful for inventory items
 * and skills); for geo/hp/feather the amount already lives on the resource
 * itself, so `count` is ignored and the amount is shown directly.
 */
export function resourceLabel(r: Resource, count = 1): string {
  switch (r.kind) {
    case "geo":
    case "hp":
    case "feather":
      return `×${r.amount}`;
    case "inventoryItem":
      return `×${count}`;
    case "skill":
      return r.skillId === "doubleJump"
        ? t("shop.skill.short.double_jump")
        : t("shop.skill.short.feather");
  }
}

export function isSkillResource(r: Resource): r is Resource & { kind: "skill"; skillId: SkillId } {
  return r.kind === "skill";
}
